import {
  v1LocalizationControllerBulkCreateTranslations,
  v1LocalizationControllerBulkCreateUiLocalizations,
  v1LocalizationControllerCreateLanguage,
  v1LocalizationControllerCreateTranslation,
  v1LocalizationControllerCreateUiLocalization,
  v1LocalizationControllerDeleteTranslation,
  v1LocalizationControllerDeleteUiLocalization,
  v1LocalizationControllerGetAllLanguages,
  v1LocalizationControllerGetAllOriginalStrings,
  v1LocalizationControllerGetAllTranslations,
  v1LocalizationControllerGetLanguageProgress,
  v1LocalizationControllerGetUiLocalizations,
  v1LocalizationControllerTranslateText,
  v1LocalizationControllerUpdateTranslation,
  v1LocalizationControllerUpdateUiLocalization,
  type LanguageResponse,
  type SetUserLanguageRequest,
  type LanguageProgressDto,
  type TranslationDto,
  v1UserControllerSetUserLanguage,
} from '~/generated/api'
import type { PreferredLanguage } from '~/models'
import {
  languageStorePersistedPaths,
  normalizeTranslationsByLanguage,
} from '~/utils/languageState'

// Batch processing configuration
const BATCH_SIZE = 50 // Process 50 strings at a time
const BATCH_DELAY = 1000 // 1 second delay between batches
const MAX_RETRIES = 2 // Maximum retries for failed batches

export const useLanguageStore = defineStore(
  'language',
  () => {
    const languages = ref<PreferredLanguage[]>([])
    const translations = ref<Map<string, string>>(new Map())
    const translationsByLanguage = ref<Map<string, Map<string, string>>>(
      new Map(),
    )
    const allOriginalStrings = ref<string[]>([])
    // Set from the language list or from the user's `LanguageResponse`,
    // which has no `code`/`isDefault`.
    const interfaceLanguage = ref<PreferredLanguage | LanguageResponse>()

    // Batch processing state
    const batchProgress = ref({
      current: 0,
      total: 0,
      isProcessing: false,
    })

    const appConfig = useAppConfig()
    const adminApiHeaders = {
      'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY,
    }
    const authStore = useAuthStore()
    const { userContext } = storeToRefs(authStore)

    async function withLoading<T>(
      loading: Ref<boolean>,
      operation: () => Promise<T>,
    ) {
      loading.value = true
      try {
        return await operation()
      }
      finally {
        loading.value = false
      }
    }

    async function fetchLanguages(fetching: Ref<boolean>) {
      // fetch report access token from query params
      const queryParams = useRoute().query
      const reportAccessToken = queryParams.reportAccessToken as string
      if (languages.value.length > 0) {
        return
      }
      const { data: responseData } = await withLoading(fetching, () =>
        v1LocalizationControllerGetAllLanguages({
          ...(reportAccessToken ? { auth: reportAccessToken } : {}),
        }))
      const data = (responseData?.languages ?? []).map(language => ({
        id: language.id,
        name: language.name,
        code: language.tag,
        isDefault: language.tag === 'en-US',
        tag: language.tag,
      }))
      if (data) {
        languages.value = data.sort((a, b) => {
          // Put English (en-US) at the front
          if (a.tag === 'en-US' && a.name === 'English')
            return -1
          if (b.tag === 'en-US' && b.name === 'English')
            return 1
          // Sort all other languages alphabetically by name
          return a.name.localeCompare(b.name)
        })
      }
    }

    async function fetchTranslations(
      fetching: Ref<boolean>,
      languageId: string,
    ) {
      if (!languageId) {
        return
      }
      // Ensure both Maps are properly initialized
      translationsByLanguage.value = normalizeTranslationsByLanguage(
        translationsByLanguage.value,
      )
      if (!(translations.value instanceof Map)) {
        translations.value = new Map()
      }

      // Check if translations are already cached for this language
      if (translationsByLanguage.value.has(languageId)) {
        translations.value = translationsByLanguage.value.get(languageId)!
        return
      }

      // fetch report access token from query params
      const queryParams = useRoute().query
      const reportAccessToken = queryParams.reportAccessToken as string
      const { data: responseData } = await withLoading(fetching, () =>
        v1LocalizationControllerGetAllTranslations({
          ...(reportAccessToken ? { auth: reportAccessToken } : {}),
          path: { languageId },
        }))
      // SDK failures have no data. Keep the previous translations and leave
      // this language uncached so setup can retry after authentication recovers.
      if (!responseData)
        return
      const data = (responseData.translations ?? []).map(source => ({
        ...source,
        translations: source.translations?.flat() ?? [],
      }))

      if (data) {
        const languageTranslations = new Map<string, string>()
        for (const item of data) {
          if (item.translations && item.translations.length > 0) {
            languageTranslations.set(
              item.originalText,
              item.translations[0].translatedText,
            )
          }
        }

        // Cache the translations for this language
        translationsByLanguage.value.set(languageId, languageTranslations)
        // Set current translations
        translations.value = languageTranslations
      }
    }

    function setActiveTranslations(
      languageTranslations: Map<string, string>,
      languageId?: string,
    ) {
      translations.value = languageTranslations
      if (!languageId)
        return

      translationsByLanguage.value = normalizeTranslationsByLanguage(
        translationsByLanguage.value,
      )
      translationsByLanguage.value.set(languageId, languageTranslations)
    }

    async function fetchUILocalizations(
      locale: string,
      reportAccessToken?: string,
      throwOnFailure = false,
    ) {
      try {
        const { data: responseData } = await withLoading(ref(false), () =>
          v1LocalizationControllerGetUiLocalizations({
            ...(reportAccessToken ? { auth: reportAccessToken } : {}),
            path: { locale },
            throwOnError: true,
          }))
        const data = responseData
        return data ?? {}
      }
      catch (error) {
        if (throwOnFailure)
          throw error
        return {}
      }
    }

    // UI Localizations (create/update/delete/bulk)
    async function createUILocalization(
      posting: Ref<boolean>,
      locale: string,
      key: string,
      value: string,
      sourceValue?: string,
    ) {
      try {
        const { data } = await withLoading(posting, () =>
          v1LocalizationControllerCreateUiLocalization({
            body: { key, value, ...(sourceValue !== undefined ? { sourceValue } : {}) },
            headers: adminApiHeaders,
            path: { locale },
            throwOnError: true,
          }))
        return data
      }
      catch (error) {
        console.error('Failed to create UI localization:', error)
        throw error
      }
    }

    async function updateUILocalization(
      posting: Ref<boolean>,
      locale: string,
      key: string,
      value: string,
      sourceValue?: string,
    ) {
      try {
        const { data } = await withLoading(posting, () =>
          v1LocalizationControllerUpdateUiLocalization({
            body: { key, value, ...(sourceValue !== undefined ? { sourceValue } : {}) },
            path: { locale },
            throwOnError: true,
          }))
        return data
      }
      catch (error) {
        console.error('Failed to update UI localization:', error)
        throw error
      }
    }

    async function deleteUILocalization(
      deleting: Ref<boolean>,
      locale: string,
      key: string,
    ) {
      try {
        const { data } = await withLoading(deleting, () =>
          v1LocalizationControllerDeleteUiLocalization({
            body: { key },
            headers: adminApiHeaders,
            path: { locale },
            throwOnError: true,
          }))
        return data
      }
      catch (error) {
        console.error('Failed to delete UI localization:', error)
        throw error
      }
    }

    async function bulkCreateUILocalizations(
      posting: Ref<boolean>,
      locale: string,
      localizations: { key: string, value: string, sourceValue?: string }[],
    ) {
      try {
        const body = { localizations }
        const { data } = await withLoading(posting, () =>
          v1LocalizationControllerBulkCreateUiLocalizations({
            body,
            headers: adminApiHeaders,
            path: { locale },
            throwOnError: true,
          }))
        return data
      }
      catch (error) {
        console.error('Failed to bulk create UI localizations:', error)
        throw error
      }
    }

    async function bulkCreateUILocalizationsBatched(
      posting: Ref<boolean>,
      locale: string,
      localizations: { key: string, value: string, sourceValue?: string }[],
      onProgress?: (current: number, total: number) => void,
    ) {
      try {
        batchProgress.value = {
          current: 0,
          total: localizations.length,
          isProcessing: true,
        }

        const batches: { key: string, value: string, sourceValue?: string }[][] = []
        for (let i = 0; i < localizations.length; i += BATCH_SIZE) {
          batches.push(localizations.slice(i, i + BATCH_SIZE))
        }

        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i]
          let retryCount = 0
          while (retryCount <= MAX_RETRIES) {
            try {
              await bulkCreateUILocalizations(posting, locale, batch)
              break
            }
            catch (error) {
              retryCount++
              console.error(
                `Failed to create UI localizations batch ${i + 1} (attempt ${retryCount}):`,
                error,
              )
              if (retryCount > MAX_RETRIES) {
                console.error(
                  `Max retries exceeded for batch ${i + 1}, skipping...`,
                )
                break
              }
              await new Promise(resolve =>
                setTimeout(resolve, BATCH_DELAY * retryCount),
              )
            }
          }

          batchProgress.value.current += batch.length
          if (onProgress)
            onProgress(batchProgress.value.current, batchProgress.value.total)

          if (i < batches.length - 1)
            await new Promise(resolve => setTimeout(resolve, BATCH_DELAY))
        }

        return true
      }
      catch (error) {
        console.error(
          'Failed to bulk create UI localizations in batches:',
          error,
        )
        throw error
      }
      finally {
        batchProgress.value.isProcessing = false
      }
    }

    async function fetchAllOriginalStrings(fetching: Ref<boolean>) {
      // Check if we already have the original strings
      if (allOriginalStrings.value.length > 0) {
        return
      }
      const { data } = await withLoading(fetching, () =>
        v1LocalizationControllerGetAllOriginalStrings({ throwOnError: true }))
      if (data) {
        allOriginalStrings.value = data.originalStrings ?? []
      }
    }

    async function fetchLanguageProgress(fetching: Ref<boolean>) {
      const { data: responseData } = await withLoading(fetching, () =>
        v1LocalizationControllerGetLanguageProgress({ throwOnError: true }))
      const data = responseData?.progress ?? []
      if (data) {
        return data as LanguageProgressDto[]
      }
      return []
    }

    async function updateUserLanguage(
      posting: Ref<boolean>,
      languageId?: string,
    ) {
      try {
        const body = { languageId } as unknown as SetUserLanguageRequest
        const { data } = await withLoading(posting, () =>
          v1UserControllerSetUserLanguage({
            body,
            throwOnError: true,
          }))
        if (data) {
          userContext.value = data
        }
      }
      catch (error) {
        console.error('Failed to update user language:', error)
      }
    }

    async function createTranslation(
      posting: Ref<boolean>,
      languageId: string,
      originalText: string,
      translatedText: string,
    ) {
      try {
        const { data: result } = await withLoading(posting, () =>
          v1LocalizationControllerCreateTranslation({
            body: { originalText, translatedText },
            headers: adminApiHeaders,
            path: { languageId },
            throwOnError: true,
          }))

        // Update both current and cached translations
        if (result) {
          if (!(translations.value instanceof Map)) {
            translations.value = new Map()
          }
          translations.value.set(originalText, translatedText)
          // Update cached translations for this language
          if (!(translationsByLanguage.value instanceof Map)) {
            translationsByLanguage.value = new Map()
          }
          if (translationsByLanguage.value.has(languageId)) {
            translationsByLanguage.value
              .get(languageId)!
              .set(originalText, translatedText)
          }
        }

        return result
      }
      catch (error) {
        console.error('Failed to create translation:', error)
        throw error
      }
    }

    async function updateTranslation(
      posting: Ref<boolean>,
      languageId: string,
      originalText: string,
      translatedText: string,
    ) {
      try {
        const { data: result } = await withLoading(posting, () =>
          v1LocalizationControllerUpdateTranslation({
            body: { originalText, translatedText },
            path: { languageId },
            throwOnError: true,
          }))

        // Update both current and cached translations
        if (result) {
          if (!(translations.value instanceof Map)) {
            translations.value = new Map()
          }
          translations.value.set(originalText, translatedText)
          // Update cached translations for this language
          if (!(translationsByLanguage.value instanceof Map)) {
            translationsByLanguage.value = new Map()
          }
          if (translationsByLanguage.value.has(languageId)) {
            translationsByLanguage.value
              .get(languageId)!
              .set(originalText, translatedText)
          }
        }

        return result
      }
      catch (error) {
        console.error('Failed to update translation:', error)
        throw error
      }
    }

    async function deleteTranslation(
      deleting: Ref<boolean>,
      languageId: string,
      originalText: string,
    ) {
      try {
        const { data } = await withLoading(deleting, () =>
          v1LocalizationControllerDeleteTranslation({
            body: { originalText },
            headers: adminApiHeaders,
            path: { languageId },
            throwOnError: true,
          }))
        const result = data as unknown

        // Remove from both current and cached translations
        if (result) {
          if (!(translations.value instanceof Map)) {
            translations.value = new Map()
          }
          translations.value.delete(originalText)
          // Remove from cached translations for this language
          if (!(translationsByLanguage.value instanceof Map)) {
            translationsByLanguage.value = new Map()
          }
          if (translationsByLanguage.value.has(languageId)) {
            translationsByLanguage.value.get(languageId)!.delete(originalText)
          }
        }

        return result
      }
      catch (error) {
        console.error('Failed to delete translation:', error)
        throw error
      }
    }

    async function createLanguage(
      posting: Ref<boolean>,
      name: string,
      code: string,
      tag: string,
    ) {
      try {
        const body = { name, code, tag }
        const { data: result } = await withLoading(posting, () =>
          v1LocalizationControllerCreateLanguage({
            body,
            headers: adminApiHeaders,
            throwOnError: true,
          }))

        // Add the new language to the languages array and sort with English first
        if (result) {
          languages.value.push({
            id: result.id,
            name: result.name,
            code: result.tag,
            isDefault: result.tag === 'en-US',
            tag: result.tag,
          })
          // Sort languages with English (en-US) first, then alphabetically by name
          languages.value.sort((a, b) => {
            // Put English (en-US) at the front
            if (a.tag === 'en-US' && a.name === 'English')
              return -1
            if (b.tag === 'en-US' && b.name === 'English')
              return 1
            // Sort all other languages alphabetically by name
            return a.name.localeCompare(b.name)
          })
        }

        return result
      }
      catch (error) {
        console.error('Failed to create language:', error)
        throw error
      }
    }

    async function translateText(
      posting: Ref<boolean>,
      text: string,
      targetLanguage: string,
      sourceLanguage?: string,
      textType: 'plain' | 'html' = 'plain',
    ) {
      try {
        const body = [
          {
            text,
            language: sourceLanguage,
            textType,
            targets: [
              {
                language: targetLanguage,
              },
            ],
          },
        ]
        const { data: responseData } = await withLoading(posting, () =>
          v1LocalizationControllerTranslateText({
            body,
            headers: adminApiHeaders,
            throwOnError: true,
          }))
        const result = responseData?.translations ?? []

        if (result && result.length > 0) {
          return result[0] as TranslationDto
        }

        return null
      }
      catch (error) {
        console.error('Failed to translate text:', error)
        throw error
      }
    }

    async function bulkTranslateTexts(
      posting: Ref<boolean>,
      texts: string[],
      targetLanguage: string,
      sourceLanguage?: string,
      textType: 'plain' | 'html' = 'plain',
    ) {
      try {
        const body = texts.map(text => ({
          text,
          language: sourceLanguage,
          textType,
          targets: [
            {
              language: targetLanguage,
            },
          ],
        }))
        const { data: responseData } = await withLoading(posting, () =>
          v1LocalizationControllerTranslateText({
            body,
            headers: adminApiHeaders,
            throwOnError: true,
          }))
        const result = responseData?.translations ?? []

        if (result && Array.isArray(result)) {
          return result as TranslationDto[]
        }

        return []
      }
      catch (error) {
        console.error('Failed to bulk translate texts:', error)
        throw error
      }
    }

    async function bulkTranslateTextsBatched(
      posting: Ref<boolean>,
      texts: string[],
      targetLanguage: string,
      sourceLanguage?: string,
      textType: 'plain' | 'html' = 'plain',
      onProgress?: (current: number, total: number) => void,
    ) {
      try {
        // Initialize batch progress
        batchProgress.value = {
          current: 0,
          total: texts.length,
          isProcessing: true,
        }

        const allResults: TranslationDto[] = []
        const batches = []

        // Split texts into batches
        for (let i = 0; i < texts.length; i += BATCH_SIZE) {
          batches.push(texts.slice(i, i + BATCH_SIZE))
        }

        // Process each batch
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i]
          let batchResults: TranslationDto[] = []
          let retryCount = 0

          // Retry logic for failed batches
          while (retryCount <= MAX_RETRIES) {
            try {
              batchResults = await bulkTranslateTexts(
                posting,
                batch,
                targetLanguage,
                sourceLanguage,
                textType,
              )
              if (batchResults.length !== batch.length)
                throw new Error('Translator returned an unexpected number of messages')
              break // Success, exit retry loop
            }
            catch (error) {
              retryCount++
              console.error(
                `Failed to translate batch ${i + 1} (attempt ${retryCount}):`,
                error,
              )

              if (retryCount > MAX_RETRIES) {
                console.error(
                  `Max retries exceeded for batch ${i + 1}, skipping...`,
                )
                // Add empty results for failed batch to maintain array alignment
                batchResults = Array(batch.length).fill(null)
                break
              }

              // Wait before retry
              await new Promise(resolve =>
                setTimeout(resolve, BATCH_DELAY * retryCount),
              )
            }
          }

          allResults.push(...batchResults)

          // Update progress
          batchProgress.value.current += batch.length
          if (onProgress) {
            onProgress(batchProgress.value.current, batchProgress.value.total)
          }

          // Add delay between batches (except for the last batch)
          if (i < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, BATCH_DELAY))
          }
        }

        return allResults
      }
      catch (error) {
        console.error('Failed to bulk translate texts in batches:', error)
        throw error
      }
      finally {
        batchProgress.value.isProcessing = false
      }
    }

    async function bulkCreateTranslations(
      posting: Ref<boolean>,
      languageId: string,
      translationsRequest: { originalText: string, translatedText: string }[],
    ) {
      try {
        const body = {
          translations: translationsRequest,
        }
        const { data: result } = await withLoading(posting, () =>
          v1LocalizationControllerBulkCreateTranslations({
            body,
            headers: adminApiHeaders,
            path: { languageId },
            throwOnError: true,
          }))

        // Update both current and cached translations
        if (result) {
          if (!(translations.value instanceof Map)) {
            translations.value = new Map()
          }
          if (!(translationsByLanguage.value instanceof Map)) {
            translationsByLanguage.value = new Map()
          }

          // Update current translations
          translationsRequest.forEach(({ originalText, translatedText }) => {
            translations.value.set(originalText, translatedText)
          })

          // Update cached translations for this language
          if (translationsByLanguage.value.has(languageId)) {
            const languageTranslations
              = translationsByLanguage.value.get(languageId)!
            translationsRequest.forEach(({ originalText, translatedText }) => {
              languageTranslations.set(originalText, translatedText)
            })
          }
        }

        return result
      }
      catch (error) {
        console.error('Failed to bulk create translations:', error)
        throw error
      }
    }

    async function bulkCreateTranslationsBatched(
      posting: Ref<boolean>,
      languageId: string,
      translationsRequest: { originalText: string, translatedText: string }[],
      onProgress?: (current: number, total: number) => void,
    ) {
      try {
        // Initialize batch progress
        batchProgress.value = {
          current: 0,
          total: translationsRequest.length,
          isProcessing: true,
        }

        const batches = []

        // Split translations into batches
        for (let i = 0; i < translationsRequest.length; i += BATCH_SIZE) {
          batches.push(translationsRequest.slice(i, i + BATCH_SIZE))
        }

        // Process each batch
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i]
          let retryCount = 0

          // Retry logic for failed batches
          while (retryCount <= MAX_RETRIES) {
            try {
              await bulkCreateTranslations(posting, languageId, batch)
              break // Success, exit retry loop
            }
            catch (error) {
              retryCount++
              console.error(
                `Failed to create translations batch ${i + 1} (attempt ${retryCount}):`,
                error,
              )

              if (retryCount > MAX_RETRIES) {
                console.error(
                  `Max retries exceeded for batch ${i + 1}, skipping...`,
                )
                break
              }

              // Wait before retry
              await new Promise(resolve =>
                setTimeout(resolve, BATCH_DELAY * retryCount),
              )
            }
          }

          // Update progress
          batchProgress.value.current += batch.length
          if (onProgress) {
            onProgress(batchProgress.value.current, batchProgress.value.total)
          }

          // Add delay between batches (except for the last batch)
          if (i < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, BATCH_DELAY))
          }
        }

        return true
      }
      catch (error) {
        console.error('Failed to bulk create translations in batches:', error)
        throw error
      }
      finally {
        batchProgress.value.isProcessing = false
      }
    }

    function clearTranslationsCache(languageId?: string) {
      // Ensure translationsByLanguage is a Map
      if (!(translationsByLanguage.value instanceof Map)) {
        translationsByLanguage.value = new Map()
      }

      if (languageId) {
        // Clear cache for specific language
        translationsByLanguage.value.delete(languageId)
        // If this was the current language, clear current translations too
        if (
          translations.value === translationsByLanguage.value.get(languageId)
        ) {
          if (!(translations.value instanceof Map)) {
            translations.value = new Map()
          }
          translations.value.clear()
        }
      }
      else {
        // Clear all cached translations
        translationsByLanguage.value.clear()
        if (!(translations.value instanceof Map)) {
          translations.value = new Map()
        }
        translations.value.clear()
      }
    }

    return {
      languages,
      translations,
      translationsByLanguage,
      allOriginalStrings,
      interfaceLanguage,
      batchProgress,
      fetchLanguages,
      updateUserLanguage,
      fetchTranslations,
      setActiveTranslations,
      fetchAllOriginalStrings,
      fetchLanguageProgress,
      createTranslation,
      updateTranslation,
      deleteTranslation,
      createLanguage,
      translateText,
      bulkTranslateTexts,
      bulkTranslateTextsBatched,
      bulkCreateTranslations,
      bulkCreateTranslationsBatched,
      clearTranslationsCache,
      fetchUILocalizations,
      createUILocalization,
      updateUILocalization,
      deleteUILocalization,
      bulkCreateUILocalizations,
      bulkCreateUILocalizationsBatched,
    }
  },
  {
    persist: {
      // Translation caches are Maps and cannot survive the plugin's JSON
      // serializer. They are inexpensive to reload and unsafe to hydrate.
      pick: [...languageStorePersistedPaths],
    },
  },
)
